import {
  Box,
  Checkbox,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import React from 'react'
import {
  TABLE_WIDTH_INVENTORY_BRAND_FILTER,
  TABLE_WIDTH_ICON,
  TABLE_WIDTH_INVENTORY_MODEL_FILTER,
  TABLE_WIDTH_INVENTORY_PARTNER,
  TABLE_WIDTH_INVENTORY_PRICE_CHANGE_COUNT,
} from 'lib/tableLib'
import config from 'config'

import DotCell from '../../atoms/DotCell'
import {
  INVENTORY_STATE_ASSIGNED,
  INVENTORY_STATE_CHECK_OK,
  INVENTORY_STATE_DELIVERED,
  INVENTORY_STATE_DOCUMENTS_RECEIVED,
  INVENTORY_STATE_FINAL_CUSTOMER_INVOICE,
  INVENTORY_STATE_FOR_SALE_ONLINE,
  INVENTORY_STATE_INVOICED,
  INVENTORY_STATE_CANCELED,
  INVENTORY_STATE_PAYMENT_CONFIRMATION,
  INVENTORY_STATE_PRICE_CHANGE_REQUEST,
  INVENTORY_STATE_PURCHASE_REQUEST,
  INVENTORY_STATE_RETURNED,
  INVENTORY_STATE_RETURNED_CAR_ARRIVED,
  INVENTORY_STATE_RETURNED_CAR_LOADED,
  INVENTORY_STATE_RETURNED_TRANSPORTATION_CONFIRMED,
  INVENTORY_STATE_RETURNED_TRANSPORTATION_REQUEST,
  INVENTORY_STATE_SOLD,
  INVENTORY_STATE_UNDER_NEGOTIATION,
} from '../../../lib/inventoryStates'
import getOption, { OPTION_ALL_VALUE } from '../../../lib/getOption'
import TableHeaderCell from '../../atoms/TableHeaderCell'
import SearchField from '../../atoms/SearchField'
import FilterSelect from '../../atoms/FilterSelect'
import FilterRange from '../../atoms/FilterRange'
import FilterDateRange from '../../atoms/FilterDateRange'
import DateCell from '../../atoms/DateCell'
import Tooltip from '../../atoms/Tooltip'

const stateCell = ({ value, row }, onOpenStateHistoryInfoDialog, t) => {
  const color = [
    INVENTORY_STATE_ASSIGNED,
    INVENTORY_STATE_DELIVERED,
    INVENTORY_STATE_FOR_SALE_ONLINE,
    INVENTORY_STATE_UNDER_NEGOTIATION,
    INVENTORY_STATE_PRICE_CHANGE_REQUEST,
    INVENTORY_STATE_RETURNED,
    INVENTORY_STATE_RETURNED_TRANSPORTATION_REQUEST,
    INVENTORY_STATE_RETURNED_TRANSPORTATION_CONFIRMED,
    INVENTORY_STATE_RETURNED_CAR_LOADED,
    INVENTORY_STATE_PURCHASE_REQUEST,
    INVENTORY_STATE_INVOICED,
    INVENTORY_STATE_CANCELED,
    INVENTORY_STATE_PAYMENT_CONFIRMATION,
    INVENTORY_STATE_SOLD,
    INVENTORY_STATE_DOCUMENTS_RECEIVED,
    INVENTORY_STATE_FINAL_CUSTOMER_INVOICE,
  ].includes(value)
    ? 'textPrimary'
    : [INVENTORY_STATE_CHECK_OK, INVENTORY_STATE_RETURNED_CAR_ARRIVED].includes(
        value
      )
    ? 'success'
    : 'textSecondary'

  const textColor = color === 'success' ? '#2BB5AE' : undefined

  return (
    <DotCell
      width="100%"
      labelProps={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1,
        width: 1,
      }}
      label={
        <>
          <Typography variant="body2" color={textColor}>
            {t(`partnerInventory.status.${value}`)}
          </Typography>
          <Box pr={1} />
          {row.original.stateHistoryList && (
            <IconButton
              data-testid="state-history-info"
              aria-label="State history info"
              onClick={(e) => onOpenStateHistoryInfoDialog(e, row.original)}
            >
              <InfoOutlinedIcon />
            </IconButton>
          )}
        </>
      }
      color={color}
    />
  )
}

export const createColumns = (
  items,
  sixtUser,
  isSelected,
  selected,
  readOnly,
  contentIsReadOnly,
  partnerInventoryFilters,
  numberFormatter,
  countryConfig,
  onOpenDocumentsToDownloadDialog,
  onOpenPriceChangesDialog,
  onOpenStateHistoryInfoDialog,
  t
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const {
    filters,
    filtersData,
    setPlateQuery,
    setVinQuery,
    setSessionIdRange,
    setDaysRange,
    setLeadRange,
    setAssignedAtRange,
    setStateChangedAtRange,
    setPriceRange,
    setPartners,
    setBrands,
    setModels,
    setChannels,
    setStates,
  } = partnerInventoryFilters

  const allOption = getOption(t('common.form.all'), OPTION_ALL_VALUE)

  const columnCheckbox = {
    accessor: 'checkbox',
    Header: () => {
      return <></>
    },
    Cell: ({ row }) => {
      if (!!contentIsReadOnly) {
        return <></>
      } else {
        return (
          <Stack direction="column" height={1} justifyContent="center">
            <Checkbox
              color="primary"
              checked={isSelected(row.original, 'inventoryId')}
              inputProps={{
                'aria-labelledby': row.original.id,
              }}
            />
          </Stack>
        )
      }
    },
    headerProps: {
      padding: 'checkbox',
      component: 'th',
    },
    cellProps: {
      padding: 'checkbox',
      component: 'th',
    },
    sticky: 'left',
    width: 52,
  }

  const getColumnVinAndPlate = (type, width) => ({
    accessor: type,
    Header: (
      <TableHeaderCell
        label={t(`partnerInventory.table.${type}`)}
        filter={
          <SearchField
            data-testid={`text-search-${type}`}
            placeholder={t('common.form.search')}
            value={filters?.[`${type}Query`]}
            onChange={(event) => {
              if (type === 'vin') {
                setVinQuery(event.target.value)
              } else {
                setPlateQuery(event.target.value)
              }
            }}
            fullWidth={false}
            sx={{ width: (theme) => theme.spacing(15) }}
          />
        }
      />
    ),
    Cell: ({ row, value }) => {
      let background = {}
      if (countryConfig.attribute.primary === type) {
        const days = row.original.days
        const previewDays = row.original.previewDays || 0
        const halfPreviewDays = previewDays / 2

        let color = days >= 0 ? '#006d1d' : 'inherited'

        if (days > previewDays) {
          color = '#c40000'
        } else if (days > halfPreviewDays) {
          color = '#d58e0a'
        }
        background.color = color
      }
      return (
        <Box>
          <Tooltip
            title={row.original.partnerAddress}
            followCursor
            placement="bottom-start"
          >
            <Box sx={{ ...background }}>{value}</Box>
          </Tooltip>
        </Box>
      )
    },
    ...(width ? { width } : {}),
    sticky: 'left',
  })

  const columnBrand = {
    accessor: 'brand',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.brand')}
        filter={
          <FilterSelect
            data-testid="filter-brands"
            allOption={allOption}
            options={filtersData.brands}
            value={filters.brands}
            handleChange={setBrands}
            multiple
          />
        }
      />
    ),
    width: TABLE_WIDTH_INVENTORY_BRAND_FILTER,
    sticky: 'left',
  }

  const columnModel = {
    accessor: 'model',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.model')}
        filter={
          <FilterSelect
            allOption={allOption}
            options={filtersData.models}
            value={filters.models}
            handleChange={setModels}
            multiple
          />
        }
      />
    ),
    width: TABLE_WIDTH_INVENTORY_MODEL_FILTER,
    sticky: 'left',
  }

  const columnSetup = {
    accessor: 'setup',
    Header: <TableHeaderCell label={t('partnerInventory.table.setup')} />,
    width: TABLE_WIDTH_INVENTORY_MODEL_FILTER,
    sticky: 'left',
  }

  const columnListPrice = {
    accessor: 'listPrice',
    Header: <TableHeaderCell label={t('partnerInventory.table.listPrice')} />,
  }

  const columnSessionId = {
    accessor: 'sessionId',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.sessionId')}
        filter={
          <FilterRange
            data-testid="range-sessionId"
            minBound={filtersData.sessionIdMin}
            maxBound={filtersData.sessionIdMax}
            min={filters.sessionIdMin}
            max={filters.sessionIdMax}
            handleRangeChange={setSessionIdRange}
            usePadding={true}
          />
        }
      />
    ),
    width: 122,
  }

  const columnAssignedAt = {
    accessor: 'assignedAt',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.assignedAt')}
        filter={
          <FilterDateRange
            value={[filters.assignedAtFrom, filters.assignedAtTo]}
            handleChange={setAssignedAtRange}
          />
        }
      />
    ),
    Cell: DateCell,
  }

  const columnState = {
    accessor: 'state',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.status')}
        filter={
          <FilterSelect
            allOption={allOption}
            options={filtersData.states}
            value={filters.states}
            handleChange={setStates}
            multiple
            multipleAllSelected
          />
        }
      />
    ),
    Cell: (data) => stateCell(data, onOpenStateHistoryInfoDialog, t),
    width: 170,
  }

  const columnPrice = {
    accessor: 'price',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.price')}
        filter={
          <FilterRange
            minBound={filtersData.priceMin}
            maxBound={filtersData.priceMax}
            min={filters.priceMin}
            max={filters.priceMax}
            handleRangeChange={setPriceRange}
          />
        }
      />
    ),
    Cell: ({ row }) => {
      let value
      if (
        row.original.state === 'price_change_request' &&
        row.original.negotiationPartnerPrice
      ) {
        value = row.original.price + row.original.fee

        return (
          <>
            <div>
              {(value && numberFormatter.format(value)) || null}
              <div style={{ fontSize: '11px', textWrap: 'nowrap' }}>
                {t('partnerInventory.table.priceChangeRequest', {
                  attribute: numberFormatter.format(
                    row.original.negotiationPartnerPrice
                  ),
                })}
              </div>
            </div>
          </>
        )
      } else if (
        row.original.state === 'under_negotiation' &&
        row.original.negotiationSixtPrice
      ) {
        value =
          (row.original.negotiationSixtPrice || row.original.price) +
          (row.original.negotiationSixtFee || row.original.fee)

        const originalPrice =
          row.original.originalPrice + row.original.originalFee

        return (
          <>
            <div>
              {(value && numberFormatter.format(value)) || null}{' '}
              {originalPrice ? (
                <s>{numberFormatter.format(originalPrice)}</s>
              ) : null}
            </div>
            <div style={{ color: 'red', fontSize: '11px' }}>
              {t('partnerInventory.table.underNegotiationEndDate', {
                attribute: format(
                  new Date(row.original.negotiationSixtExpire),
                  'dd/MM/yy'
                ),
              })}
            </div>
          </>
        )
      } else {
        value =
          (row.original.sellPrice || row.original.price) +
          (row.original.sellFee || row.original.fee)
        return (value && numberFormatter.format(value)) || null
      }
    },
    width: 122,
  }

  const columnSixtPrice = {
    accessor: 'sixtPrice',
    Header: <TableHeaderCell label={t('partnerInventory.table.sixtPrice')} />,
    Cell: ({ row }) => {
      let value
      if (
        row.original.state === 'price_change_request' &&
        row.original.negotiationPartnerPrice
      ) {
        value = row.original.price
      } else if (
        row.original.state === 'under_negotiation' &&
        row.original.negotiationSixtPrice
      ) {
        value = row.original.negotiationSixtPrice || row.original.price
      } else {
        value = row.original.sellPrice || row.original.price
      }
      return (value && numberFormatter.format(value)) || null
    },
    width: 122,
  }

  const columnFee = {
    accessor: 'fee',
    Header: <TableHeaderCell label={t('partnerInventory.table.fee')} />,
    Cell: ({ row }) => {
      let value
      let originalFee
      if (
        row.original.state === 'under_negotiation' &&
        row.original.negotiationSixtFee
      ) {
        value = row.original.negotiationSixtFee

        originalFee = row.original.originalFee
      } else {
        value = row.original.sellFee || row.original.fee
      }

      return (
        <>
          {(value && numberFormatter.format(value)) || null}{' '}
          {originalFee ? <s>{numberFormatter.format(originalFee)}</s> : null}
        </>
      )
    },
    width: 85,
  }

  const columnModelBestPrice = {
    accessor: 'modelBestPrice',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.modelBestPrice')}
        labelProps={{ sx: { textWrap: 'auto' } }}
      />
    ),
    Cell: ({ value, row }) =>
      (value && (
        <Box>
          <Tooltip
            title={
              <Box>
                <Typography variant="inherit">
                  {t('partnerInventory.table.tooltip.modelBestPrice.car', {
                    attribute:
                      row.original.maxSell?.[countryConfig.attribute.primary],
                  })}
                </Typography>
                <Typography variant="inherit">
                  {t('partnerInventory.table.tooltip.modelBestPrice.km', {
                    attribute:
                      row.original.maxSell?.km &&
                      numberFormatter.format(row.original.maxSell.km),
                  })}
                </Typography>
              </Box>
            }
            followCursor
            placement="bottom-start"
          >
            <Box>{numberFormatter.format(value)}</Box>
          </Tooltip>
        </Box>
      )) ||
      '-',
  }

  const columnAveragePrice = {
    accessor: 'averagePrice',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.averagePrice')}
        labelProps={{ sx: { textWrap: 'auto' } }}
      />
    ),
    Cell: ({ value, row }) =>
      (value && (
        <Box>
          <Tooltip
            title={
              <Box>
                <Typography variant="inherit">
                  {t('partnerInventory.table.tooltip.averagePrice.numPlates', {
                    attribute: row.original.avgSell?.numPlates,
                  })}
                </Typography>
                <Typography variant="inherit">
                  {t('partnerInventory.table.tooltip.averagePrice.avgDays', {
                    attribute: row.original.avgSell?.avgDays,
                  })}
                </Typography>
              </Box>
            }
            followCursor
            placement="bottom-start"
          >
            <Box>{numberFormatter.format(value)}</Box>
          </Tooltip>
        </Box>
      )) ||
      '-',
  }

  const columnDays = {
    accessor: 'days',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.days')}
        filter={
          <FilterRange
            minBound={filtersData.daysMin}
            maxBound={filtersData.daysMax}
            min={filters.daysMin}
            max={filters.daysMax}
            handleRangeChange={setDaysRange}
          />
        }
      />
    ),
  }

  const columnLead = {
    accessor: 'lead',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.lead')}
        filter={
          <FilterRange
            minBound={filtersData.leadMin}
            maxBound={filtersData.leadMax}
            min={filters.leadMin}
            max={filters.leadMax}
            handleRangeChange={setLeadRange}
          />
        }
      />
    ),
    width: 122,
  }

  const columnLinkAppraisal = {
    accessor: 'linkAppraisal',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.linkAppraisal')}
        labelProps={{ sx: { textWrap: 'auto' } }}
      />
    ),
    Cell: ({ value }) =>
      value && (
        <Link
          data-testid="car-detail-condition-of-use-report"
          href={value}
          target="_new"
        >
          <AssignmentOutlinedIcon />
        </Link>
      ),
    width: TABLE_WIDTH_ICON + 30,
  }

  const columnRepairState = {
    accessor: 'repairState',
    Header: <TableHeaderCell label={t('partnerInventory.table.repair')} />,
    Cell: ({ value, row }) => {
      const Container =
        row.original.repairNote && value !== 'requested' ? Tooltip : Box

      return (
        <Box>
          <Container
            {...(row.original.repairNote && value !== 'requested'
              ? {
                  title: row.original.repairNote,
                  followCursor: true,
                  placement: 'bottom-start',
                }
              : {})}
          >
            <span>{t(`partnerInventory.repairState.${value}`)}</span>
          </Container>
        </Box>
      )
    },
    width: 120,
  }

  const columnPriceChangeCount = {
    accessor: 'priceChangeCount',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.hasPriceChanges')}
        labelProps={{ sx: { textWrap: 'auto' } }}
      />
    ),
    Cell: ({ value, row }) => {
      return (
        value > 1 && (
          <Box
            textAlign="center"
            onClick={(e) => onOpenPriceChangesDialog(e, row.original)}
          >
            {value ? value - 1 : ''}
          </Box>
        )
      )
    },
    width: TABLE_WIDTH_INVENTORY_PRICE_CHANGE_COUNT + 20,
  }

  const columnLeadUpdateAt = {
    accessor: 'leadUpdateAt',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.leadUpdateAt')}
        labelProps={{ sx: { textWrap: 'auto' } }}
      />
    ),
    Cell: DateCell,
    width: 142,
  }

  const columnDocumentsToDownload = {
    accessor: 'documentsToDownload',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.documentsToDownload')}
      />
    ),
    Cell: ({ row }) => (
      <IconButton
        data-testid="documents-to-download"
        aria-label="Documents to download"
        onClick={(e) => onOpenDocumentsToDownloadDialog(e, row.original)}
      >
        <DescriptionOutlinedIcon />
      </IconButton>
    ),
    width: TABLE_WIDTH_ICON + 30,
  }

  const columnStateChangedAt = {
    accessor: 'stateChangedAt',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.stateChangedAt')}
        filter={
          <FilterDateRange
            value={[filters.stateChangedAtFrom, filters.stateChangedAtTo]}
            handleChange={setStateChangedAtRange}
          />
        }
      />
    ),
    Cell: DateCell,
    width: 180,
  }

  const columnChannel = {
    accessor: 'channel',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.channel')}
        filter={
          <FilterSelect
            allOption={allOption}
            options={filtersData.channels}
            value={filters.channels}
            handleChange={setChannels}
            multiple
          />
        }
      />
    ),
    Cell: ({ value }) => t(`partnerInventory.channel.${value}`),
    width: 120,
  }

  const columnPartner = {
    accessor: 'partner',
    Header: (
      <TableHeaderCell
        label={t('partnerInventory.table.partner')}
        filter={
          <FilterSelect
            allOption={allOption}
            options={filtersData.partners}
            value={filters.partners}
            handleChange={setPartners}
            multiple
          />
        }
      />
    ),
    width: TABLE_WIDTH_INVENTORY_PARTNER,
    sticky: 'left',
  }

  const resultByCountry = {
    it: [
      ...(!contentIsReadOnly ? [columnCheckbox] : []),
      ...(sixtUser ? [columnPartner] : []),
      ...(countryConfig.tableShow.vin
        ? [getColumnVinAndPlate('vin', 190)]
        : []),
      ...(countryConfig.tableShow.plate ? [getColumnVinAndPlate('plate')] : []),
      columnBrand,
      columnModel,
      columnSessionId,
      columnAssignedAt,
      columnState,
      columnPrice,
      columnFee,
      ...(sixtUser ? [columnModelBestPrice, columnAveragePrice] : []),
      columnDays,
      columnLead,
      columnLinkAppraisal,
      columnRepairState,
      columnPriceChangeCount,
      columnLeadUpdateAt,
      columnDocumentsToDownload,
      columnStateChangedAt,
      columnChannel,
    ],
    de: [
      ...(!contentIsReadOnly ? [columnCheckbox] : []),
      ...(sixtUser ? [columnPartner] : []),
      getColumnVinAndPlate('vin', 190),
      columnBrand,
      columnModel,
      { ...columnSessionId, sticky: 'left' },
      columnAssignedAt,
      columnState,
      columnPrice,
      columnFee,
      ...(sixtUser ? [columnModelBestPrice, columnAveragePrice] : []),
      columnDays,
      columnLead,
      columnLinkAppraisal,
      columnRepairState,
      columnPriceChangeCount,
      columnLeadUpdateAt,
      columnDocumentsToDownload,
      columnStateChangedAt,
      columnChannel,
    ],
    fr: [
      ...(!contentIsReadOnly ? [columnCheckbox] : []),
      ...(sixtUser ? [columnPartner] : []),
      getColumnVinAndPlate('vin', 190),
      columnBrand,
      columnModel,
      { ...columnSessionId, sticky: 'left' },
      columnAssignedAt,
      columnState,
      columnPrice,
      columnFee,
      ...(sixtUser ? [columnModelBestPrice, columnAveragePrice] : []),
      columnDays,
      columnLead,
      columnLinkAppraisal,
      columnRepairState,
      columnPriceChangeCount,
      columnLeadUpdateAt,
      columnDocumentsToDownload,
      columnStateChangedAt,
      columnChannel,
    ],
  }

  return resultByCountry[config.country ?? 'it']
}
